import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


// Configure axios defaults
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ============================
// PERSONAL DOCUMENT API CALLS
// ============================

// Upload single personal document
export const uploadPersonalDocument = async (employeeId, formData) => {
    try {
        const response = await api.post(`/employees/${employeeId}/documents/personal`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Get all personal documents for employee
export const getPersonalDocuments = async (employeeId) => {
    try {
        const response = await api.get(`/employees/${employeeId}/documents/personal`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Get documents by category
export const getPersonalDocumentsByCategory = async (employeeId, category) => {
    try {
        const response = await api.get(`/employees/${employeeId}/documents/personal/category/${category}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Update personal document metadata
export const updatePersonalDocument = async (employeeId, documentId, updateData) => {
    try {
        const response = await api.put(`/employees/${employeeId}/documents/personal/${documentId}`, updateData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Delete personal document
export const deletePersonalDocument = async (employeeId, documentId) => {
    try {
        const response = await api.delete(`/employees/${employeeId}/documents/personal/${documentId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Bulk upload personal documents
export const bulkUploadPersonalDocuments = async (employeeId, formData) => {
    try {
        const response = await api.post(`/employees/${employeeId}/documents/personal/bulk`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// ============================
// HELPER FUNCTIONS
// ============================

// Download document
export const downloadPersonalDocument = async (documentUrl, filename) => {
    try {
        const response = await fetch(documentUrl);
        if (!response.ok) throw new Error('Download failed');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        throw error;
    }
};

// Get document categories with metadata
export const getDocumentCategories = () => {
    return [
        { id: 'nufus_cuzdani', label: 'Nüfus Cüzdanı', required: true, color: 'danger' },
        { id: 'ikametgah', label: 'İkametgah Belgesi', required: true, color: 'warning' },
        { id: 'adli_sicil', label: 'Adli Sicil Kaydı', required: true, color: 'info' },
        { id: 'fotograf', label: 'Fotoğraf', required: true, color: 'primary' },
        { id: 'sgk_bilgileri', label: 'SGK Bilgileri', required: false, color: 'success' },
        { id: 'izin_belgeleri', label: 'İzin Belgeleri', required: false, color: 'secondary', multiple: true },
        { id: 'diger', label: 'Diğer', required: false, color: 'dark' }
    ];
};

// Validate file before upload
export const validateDocumentFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp'
    ];

    if (file.size > maxSize) {
        throw new Error(`${file.name} dosyası çok büyük. Maksimum 10MB yüklenebilir.`);
    }

    if (!allowedTypes.includes(file.type)) {
        throw new Error(`${file.name} desteklenmeyen format. PDF, JPG, PNG kullanın.`);
    }

    return true;
};

// Format file size
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Get file icon based on type
export const getFileIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return 'FaFilePdf';
    if (mimeType?.startsWith('image/')) return 'FaFileImage';
    return 'FaFileAlt';
};

export default {
    uploadPersonalDocument,
    getPersonalDocuments,
    getPersonalDocumentsByCategory,
    updatePersonalDocument,
    deletePersonalDocument,
    bulkUploadPersonalDocuments,
    downloadPersonalDocument,
    getDocumentCategories,
    validateDocumentFile,
    formatFileSize,
    getFileIcon
};